import {
  API_TTask,
  API_TTasks,
  convertNewTaskForTable,
  convertTaskForTable,
  convertTaskForTemplate,
  TNewTask_Table,
  TTask_Table,
  TTask_Template,
  TTask_User,
} from "@/models/tasks";
import axios from "@/plugins/http";
import { API_ROUTES } from "@/utils/API";
import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import {
  ARCHIVED_TASK_STATUS,
  ARCHIVED_TASK_STATUS_LABEL,
  COMPLETED_TASK_STATUS,
  COMPLETED_TASK_STATUS_LABEL,
  TO_DO_TASK_STATUS,
} from "@/constants/tasks";

@Module({ name: "MTasks", namespaced: true })
export default class MTasks extends VuexModule {
  successPopup = false;
  flowIds: number[] = [];
  targetUserId = 0;

  tabName = "";

  loading = false;

  employerTasks: TTask_Table[] = [];
  candidateTasks: TTask_Table[] = [];

  employerTasksToDo: TTask_Table[] = [];
  candidateTasksToDo: TTask_Table[] = [];

  employerTasksArchived: TTask_Table[] = [];
  candidateTasksArchived: TTask_Table[] = [];

  myTasks: TNewTask_Table[] = [];
  myTasksCompleted: TNewTask_Table[] = [];

  sideTasks: TNewTask_Table[] = [];
  sideTasksCompleted: TNewTask_Table[] = [];

  @Mutation
  setLoading(value: boolean) {
    this.loading = value;
  }

  @Mutation
  setMyTasks(data: API_TTasks) {
    const tasks = data.tasks?.map((a) => convertNewTaskForTable(a)) ?? [];

    const activeTasks = [...tasks].filter((item) => item.status !== COMPLETED_TASK_STATUS);
    const completedTasks = [...tasks].filter((item) => item.status === COMPLETED_TASK_STATUS);

    this.myTasks = activeTasks;
    this.myTasksCompleted = completedTasks;
  }

  @Mutation
  setSideTasks(data: API_TTasks) {
    const tasks = data.tasks?.map((a) => convertNewTaskForTable(a)) ?? [];

    const activeTasks = [...tasks].filter((item) => item.status !== COMPLETED_TASK_STATUS);
    const completedTasks = [...tasks].filter((item) => item.status === COMPLETED_TASK_STATUS);

    this.sideTasks = activeTasks;
    this.sideTasksCompleted = completedTasks;
  }

  @Mutation
  setEmployerTasks(data: API_TTask[], params: any) {
    const preparedData = data.map((a) => convertTaskForTable(a));

    const hasOffset =
      params && Object.prototype.hasOwnProperty.call(params, "offset") && params?.offset > 0;

    hasOffset ? this.employerTasks.push(...preparedData) : (this.employerTasks = preparedData);
  }

  @Mutation
  setCandidateTasks(data: API_TTask[], params: any) {
    const preparedData = data.map((a) => convertTaskForTable(a));

    const hasOffset =
      params && Object.prototype.hasOwnProperty.call(params, "offset") && params?.offset > 0;

    hasOffset ? this.candidateTasks.push(...preparedData) : (this.candidateTasks = preparedData);
  }

  @Mutation
  setEmployerTasksToDo(data: API_TTasks) {
    if (!data.my_tasks) return;

    this.employerTasksToDo = data.my_tasks.map((a) => convertTaskForTable(a));
  }

  @Mutation
  setCandidateTasksToDo(data: API_TTasks) {
    if (!data.side_tasks) return;

    this.candidateTasksToDo = data.side_tasks.map((a) => {
      return convertTaskForTable(a);
    });
  }

  @Mutation
  setMainTasksArchived(data: API_TTasks) {
    if (!data.my_tasks) return;

    this.employerTasksArchived = data.my_tasks
      .filter((a) => {
        return [
          ARCHIVED_TASK_STATUS,
          ARCHIVED_TASK_STATUS_LABEL,
          COMPLETED_TASK_STATUS,
          COMPLETED_TASK_STATUS_LABEL,
        ].includes(a.status);
      })
      .map((a) => convertTaskForTable(a));
  }

  @Mutation
  setSideTasksArchived(data: API_TTasks) {
    if (!data.side_tasks) return;

    this.candidateTasksArchived = data.side_tasks
      .filter((a) =>
        [
          ARCHIVED_TASK_STATUS,
          ARCHIVED_TASK_STATUS_LABEL,
          COMPLETED_TASK_STATUS,
          COMPLETED_TASK_STATUS_LABEL,
        ].includes(a.status)
      )
      .map((a) => convertTaskForTable(a));
  }

  taskTemplate: TTask_Template | null = null;

  @Mutation
  setTaskTemplate(payload: API_TTask) {
    this.taskTemplate = convertTaskForTemplate(payload);
  }

  @Mutation
  unsetTaskTemplate() {
    this.taskTemplate = null;
  }

  taskUserflows: number[] | null = [];

  get taskUserflow() {
    if (this.taskUserflows && this.taskUserflows.length > 0) {
      return this.taskUserflows[0];
    }
    return null;
  }

  @Mutation
  setTaskUserflows(payload: API_TTask) {
    if (payload.user_flow_ids) {
      this.taskUserflows = payload.user_flow_ids
        .split(",")
        .filter((v) => !!v)
        .map((v) => parseInt(v));
    }
  }

  @Mutation
  setRoleFlow(id: number) {
    if (!id) return;

    this.taskUserflows = [id];
  }

  @Mutation
  clearTaskUserflows() {
    this.taskUserflows = [];
  }

  @Mutation
  openSuccessPopup() {
    this.successPopup = true;
  }

  @Mutation
  closeSuccessPopup() {
    this.successPopup = false;
  }

  @Mutation
  setFlowIds(payload: API_TTask) {
    this.flowIds = payload.user_flow_ids.split(",").map((v) => parseInt(v));
  }

  @Mutation
  setTargetUserId(payload: API_TTask) {
    this.targetUserId = payload.target_user_id;
  }

  @Mutation
  setTabName(payload: string) {
    this.tabName = payload;
  }

  @Mutation
  unsetTabName() {
    this.tabName = "";
  }

  targetUser: TTask_User = {
    ID: 0,
    role: "",
  };

  @Mutation
  setTargetUser(payload: API_TTask) {
    this.targetUser = payload.target_user;
  }

  user: TTask_User = {
    ID: 0,
    role: "",
  };

  @Mutation
  setUser(payload: API_TTask) {
    this.user = payload.user;
  }

  @Action
  async getTaskTemplate(id: number | string) {
    if (!id) return;

    this.clearTaskUserflows();

    const { data } = await axios.get<API_TTask>(API_ROUTES.TASK(id));

    this.setTaskTemplate(data);

    this.setTaskUserflows(data);

    this.setFlowIds(data);

    this.setTargetUserId(data);

    this.setTargetUser(data);

    this.setUser(data);
  }

  @Action
  async finishTask(id: number | string) {
    try {
      await axios.get(API_ROUTES.FINISH_TASK(id));

      this.openSuccessPopup();
    } catch (err) {
      this.closeSuccessPopup();
    }
  }

  @Action
  async deleteTableTask(id: number | string) {
    await axios.delete(API_ROUTES.TASK(id));
  }

  @Action
  async archiveTableTask(id: number | string) {
    try {
      this.context.commit("setLoading", true);

      await axios.get(API_ROUTES.ARCHIVE_TASK(id));
    } finally {
      this.context.commit("setLoading", false);
    }
  }

  companyId: number | null = null;

  @Mutation
  setCompanyId(id: number) {
    this.companyId = id;
  }

  @Action
  async getTableTasks(params: {
    target?: string;
    limit?: number;
    offset?: number;
    all?: boolean;
    is_compliance?: boolean;
    status?: string;
  }) {
    try {
      this.setLoading(true);

      const isCompany = this.companyId;
      //
      // Object.assign(params, {
      //   status: TO_DO_TASK_STATUS,
      // });

      if (isCompany) {
        if (params.is_compliance) {
          Object.assign(params, {
            compliance_page: true,
          });
        }
      }

      const url = isCompany
        ? API_ROUTES.EXPERT_COMPANIES_TASKS_NEW(this.companyId)
        : API_ROUTES.TASKS();

      const { data } = await axios.get<API_TTasks>(url, { params });

      if (!data) return;

      return Promise.resolve({ data: data?.tasks });
    } finally {
      this.setLoading(false);
    }
  }

  @Action
  async getTableTasksToDo(isCompliance: boolean | null = null) {
    try {
      this.setLoading(true);

      const isCompany = this.companyId;

      const params = {
        status: TO_DO_TASK_STATUS,
      };

      if (isCompany) {
        if (isCompliance) {
          Object.assign(params, {
            compliance_page: true,
          });
        }
      }
      const url = isCompany
        ? API_ROUTES.EXPERT_COMPANIES_TASKS(this.companyId)
        : API_ROUTES.TASKS();

      const { data } = await axios.get<API_TTasks>(url, { params });

      if (!data) return;

      this.setEmployerTasksToDo(data);
      this.setCandidateTasksToDo(data);
    } finally {
      this.setLoading(false);
    }
  }

  @Action
  async getTableTasksArchived() {
    try {
      this.setLoading(true);

      const params = {
        status: ARCHIVED_TASK_STATUS,
      };

      const url = this.companyId
        ? API_ROUTES.EXPERT_COMPANIES_TASKS(this.companyId)
        : API_ROUTES.TASKS();

      const { data } = await axios.get<API_TTasks>(url, { params });

      if (!data) return;

      this.setMainTasksArchived(data);
      this.setSideTasksArchived(data);
    } finally {
      this.setLoading(false);
    }
  }

  @Action
  async getMyTasks(payload: {
    role?: string;
    isCompliance?: boolean | null;
    excludeStatuses?: string[];
  }) {
    const { role, excludeStatuses } = payload;

    const url = API_ROUTES.TASKS_BY_FILTER();

    if (!role) return;

    try {
      this.setLoading(true);

      const params = {
        list_name: role,
        offset: 0,
        limit: 1000,
      };

      if (excludeStatuses?.length) {
        Object.assign(params, {
          exclude_statuses: excludeStatuses,
        });
      }

      const { data } = await axios.get<API_TTasks>(url, { params });

      if (!data) return;

      this.setMyTasks?.(data);
    } finally {
      this.setLoading(false);
    }
  }

  @Action
  async getSideTasks(payload: {
    role?: string;
    isCompliance?: boolean | null;
    excludeStatuses?: string[];
  }) {
    const { role, isCompliance, excludeStatuses } = payload;

    const url = API_ROUTES.TASKS_BY_FILTER();

    if (!role) return;

    try {
      this.setLoading(true);

      const params = {
        list_name: role,
        compliance_page: !!isCompliance,
        offset: 0,
        limit: 1000,
      };

      if (excludeStatuses?.length) {
        Object.assign(params, {
          exclude_statuses: excludeStatuses,
        });
      }

      const { data } = await axios.get<API_TTasks>(url, { params });

      if (!data) return;

      this.setSideTasks(data);
    } finally {
      this.setLoading(false);
    }
  }
}
